import { z } from 'zod';
import { nameSchema } from '../../validation';
import { lenderContactSchema } from './lender.contact';
import {
  createModel,
  imageWithUploadSchema,
  statusCommonSchema,
} from '../../common';

/**
 * `Lender` db model — admin-managed directory of financing partners.
 *
 * Stored in the `lenders` collection. The lender's Mongo `_id` is the
 * canonical, single identifier; references from
 * `FinancingApplication.offers[].lenderId` point at it directly. There
 * is no separate `partnerId` — admin creates lenders via the admin
 * panel; the new doc's `_id` is the only id every other layer needs.
 *
 * `status` (from `statusCommonSchema`) drives public visibility. The
 * public `GET /lenders/active` endpoint filters `doc.status === 'active'`
 * and projects only display-safe fields (no `contacts`, no audit). The
 * admin panel can toggle a lender to `blocked` to hide it from carriers
 * without losing the row.
 *
 * Embedded `contacts[]` (see `lender.contact.ts`) holds the people Hero
 * notifies when a new application is sent to this lender. Only contacts
 * with `receiveNewApplicationEvent === true` are emailed by
 * `notifyLenderInvites`.
 *
 * Expected indexes (operator-applied):
 * - `{ slug: 1 }` unique — admin-side lookup.
 * - `{ 'doc.status': 1 }` — public `getActive` filter.
 * - Atlas Search text index over `name` + `nameAR`.
 */
export const lenderSchema = createModel({
  /** URL-safe slug, e.g. `"tarmeez"`. Unique across the collection. */
  slug: z
    .string()
    .trim()
    .min(1)
    .regex(/^[a-z][a-z0-9-]*$/),

  /** Display name in English. */
  name: nameSchema,

  /** Display name in Arabic. */
  nameAR: nameSchema,

  /** One-line marketing copy in English. */
  tagline: nameSchema,

  /** One-line marketing copy in Arabic. */
  taglineAR: nameSchema,

  /** Tailwind-safe hex color used as the lender's accent strip / brand chip. */
  brandColor: z
    .string()
    .trim()
    .regex(/^#[0-9A-Fa-f]{6}$/),

  /**
   * Visibility status. `active` lenders appear in the public
   * `getActive` listing; `blocked` lenders are hidden from carriers
   * but kept for historical reference / future re-activation.
   */
  status: statusCommonSchema,

  /**
   * People Hero notifies on lender events. Each contact has its own
   * `receiveNewApplicationEvent` flag — only flagged contacts receive
   * the `financing_lender_invite` email when a new application is
   * routed to this lender.
   */
  contacts: z.array(lenderContactSchema),

  /** Brand icon / logo. Uploaded via the admin panel. */
  logoIcon: imageWithUploadSchema,

  /** Public marketing URL, e.g. `"https://tarmeez.co"`. */
  website: z.string().url(),
});

export type Lender = z.infer<typeof lenderSchema.model>;
export type LenderData = z.infer<typeof lenderSchema.data>;
export type LenderPartial = z.infer<typeof lenderSchema.partial>;
